import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faMapMarkerAlt, faClipboardList, faLeaf, faUsers, faChartLine, faBook, faRupeeSign, faTachometerAlt } from "@fortawesome/free-solid-svg-icons";
import { Scanner } from '@yudiel/react-qr-scanner';

const TABS = [
  { id: "overview", label: "Overview", icon: faLeaf },
  { id: "field", label: "Field Ops", icon: faMapMarkerAlt },
  { id: "qrscan", label: "QR Scan", icon: faClipboardList },
  { id: "shg", label: "SHGs", icon: faUsers },
  { id: "training", label: "Training", icon: faBook },
  { id: "analytics", label: "Analytics", icon: faChartLine },
  { id: "earnings", label: "Tribal Earnings", icon: faRupeeSign },
  { id: "dashboard", label: "My Dashboard", icon: faTachometerAlt }
];

const KPI_CARDS = [
  { id: 1, title: "Plantations", value: "24,320", sub: "Geo-tagged" },
  { id: 2, title: "Survival Rate", value: "78%", sub: "QR-verified" },
  { id: 3, title: "SHGs Onboarded", value: "1,120", sub: "VanDhan & JFMCs" },
  { id: 4, title: "Training Hours", value: "9,450", sub: "RPL & PMKVY" }
];

const DUMMY_FIELDS = Array.from({ length: 8 }).map((_, i) => ({
  id: i + 1,
  name: `Range ${i + 1}`,
  division: i % 2 === 0 ? "Kanker" : "Dhamtari",
  plantations: Math.floor(500 + Math.random() * 3000),
  lastVisit: `${Math.floor(Math.random() * 30) + 1} days ago`
}));

export default function VanMitraHine() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [query, setQuery] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("All");
  // const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [qrResult, setQrResult] = useState('');

  // simple derived filtered list
  const filteredFields = useMemo(() => {
    const lower = query.toLowerCase();
    return DUMMY_FIELDS.filter((f) =>
      (selectedDivision === "All" || f.division === selectedDivision) &&
      (f.name.toLowerCase().includes(lower) || f.division.toLowerCase().includes(lower))
    );
  }, [query, selectedDivision]);

  function showToast(message) {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  }

  const handleScan = (result) => {
    if (result) {
      setQrResult(result);
      showToast(`Scanned: ${result}`);
      navigate(`/verify?code=${result}`);
    }
  };

  const handleError = (err) => {
    console.error(err);
    showToast('Error scanning QR code');
  };

  return (
    <div className="min-h-screen bg-black text-white p-4 sm:p-6">
      {/* Header */}
      <header className="flex flex-row">
        <div className="flex flex-col items-start justify-start text-left">
          <h1 className="flex text-start text-xl font-bold text-yellow-200">VanMitra</h1>
          <p className="text-xs text-white">Forest Productivity & Accountability [State: Chhattisgarh]</p>
        </div>
      </header>

      {/* Search & Quick filters */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mt-4"
      >
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-3 text-white" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search ranges, divisions, JFMCs..."
              className="h-10 pl-10 pr-3 py-3 w-full rounded-lg bg-gray-300 placeholder:text-black border border-blue-200"
            />
          </div>
          <select
            value={selectedDivision}
            onChange={(e) => setSelectedDivision(e.target.value)}
            className="w-36 h-10 py-3 rounded-lg bg-gray-300 border text-black border-green-500 text-sm"
          >
            <option>All</option>
            <option>Kanker</option>
            <option>Dhamtari</option>
            <option>Dantewada</option>
          </select>
        </div>

        {/* Tabs */}
        <div className="mt-4">
          <div className="grid grid-cols-3 lg:grid-cols-4 gap-0 place-items-center">
            {TABS.map((t) => (
                <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex flex-col sm:flex-row items-center justify-center gap-1 py-2 px-1 rounded-lg text-xs sm:text-sm font-medium cursor-pointer transition-colors w-full ${
                    activeTab === t.id
                    ? "bg-green-800 text-white shadow"
                    : "bg-gray-300 text-green-800 border border-green-200 hover:bg-green-50 hover:border-green-300"
                }`}
                aria-pressed={activeTab === t.id}
                >
                <FontAwesomeIcon icon={t.icon} className="flex-shrink-0" />
                <span className="text-center">{t.label}</span>
                </button>
            ))}
            </div>
        </div>
      </motion.div>

      {/* Main content area */}
      <main className="mt-4 grid grid-cols-1 gap-4">
        {/* Overview Tab */}
        {activeTab === "overview" && (
            <>
          
          <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 bg-gray-800 border border-green-400 rounded-lg">
            {/* KPI cards */}
            <div className="flex flex-col items-start justify-between">
              <h2 className="text-lg font-semibold text-yellow-200">Overview</h2>
              <div className="text-sm text-white">Last sync: 12 min ago (dummy)</div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 place-items-center">
              {KPI_CARDS.map((c) => (
                <motion.div
                  key={c.id}
                  whileHover={{ scale: 1.02 }}
                  className="p-3 rounded-xl bg-gray-800 border border-green-500 shadow-sm"
                >
                  <div className="text-xs text-white">{c.title}</div>
                  <div className="text-2xl font-bold text-white mt-1">{c.value}</div>
                  <div className="text-xs text-white mt-2">{c.sub}</div>
                </motion.div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3 place-items-center">
              <button
                onClick={() => navigate('/field-monitor')}
                className="py-3 rounded-lg bg-green-800 text-white text-sm flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faMapMarkerAlt} /> Field Monitor
              </button>
              <button
                onClick={() => navigate('/community-portal')}
                className="py-3 rounded-lg bg-green border border-white text-white text-sm flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faUsers} /> Community Portal
              </button>
            </div>

            {/* Map + Recent Activities (stacked on mobile) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div className="sm:col-span-2 rounded-xl overflow-hidden bg-gray-300 border border-blue-100">
                <div className="p-3 bg-black text-white flex flex-col items-center justify-between">
                  <div className="flex flex-col">
                    <div className="text-sm font-semibold text-white">Field Map (Placeholder)</div>
                    <div className="text-xs text-white">Geo-tagged plantations, fire alerts, NTFP pickup points</div>
                  </div>
                  <button onClick={() => navigate('/map')} className="mt-2 w-20 text-sm bg-blue-500 px-2 py-1 rounded">
                    Open Map
                  </button>
                </div>
                <div className="h-44 bg-black border border-green-500 flex items-center justify-center text-white">
                  Map / GIS placeholder
                </div>
              </div>

              <div className="rounded-xl bg-black border border-blue-100 p-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-yellow-200">Recent Field Activities</div>
                  <div className="text-xs text-white">Live-ish · Dummy</div>
                </div>
                <ul className="mt-2 space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500 mt-2"></span>
                    <div>
                      <div className="font-medium text-white">Plantation verified via QR</div>
                      <div className="text-xs text-white">Range 3 — 2 days ago</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-yellow-400 mt-2"></span>
                    <div>
                      <div className="font-medium text-white">Training session completed</div>
                      <div className="text-xs text-white">SHG: Van Dhan A — 5 members certified</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-400 mt-2"></span>
                    <div>
                      <div className="font-medium text-white">Fire alert (resolved)</div>
                      <div className="text-xs text-white">Range 6 — 1 week ago</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Callout for Pilot Request & Integrations */}
            <div className="rounded-xl p-3 bg-brown-50 border border-blue-100 text-white">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <div className="font-semibold text-yellow-200">Ongoing Activities</div>
                  <div className="text-sm text-white mt-1">2-3 month pilot in Kanker or Dhamtari. To integrate CAMPA, NTFP MIS and Skill portals when ready.</div>
                </div>
                {/* <div className="flex items-center gap-2">
                  <button onClick={() => navigate('/pilot-approval')} className="px-3 py-2 rounded bg-green-700 text-white text-sm">
                    Approve Pilot
                  </button>
                </div> */}
              </div>
            </div>
          </motion.section>
          </>
        )}

        {/* Field Ops Tab */}
        {activeTab === "field" && (
          <section className="space-y-3 bg-gray-600 rounded-lg border border-green-500">
            <div className="flex flex-col items-start justify-between">
              <h2 className="text-lg font-semibold text-yellow-200">Field Operations</h2>
              <div className="text-sm text-white">Last sync: 12 min ago (dummy)</div>
            </div>

            <div className="space-y-2">
              {filteredFields.map((f) => (
                <motion.div whileHover={{ scale: 1.01 }} key={f.id} className="p-3 rounded-xl bg-gray-800 border border-blue-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-white">{f.name}</div>
                      <div className="text-xs text-white">Division: {f.division}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-white">{f.plantations}</div>
                      <div className="text-xs text-white">{f.lastVisit}</div>
                    </div>
                  </div>

                  <div className="mt-3 flex gap-2">
                    <button className="flex-1 py-2 w-40 rounded bg-green-700 text-white text-sm" onClick={() => navigate(`/inspect/${f.id}`)}>
                      Inspect
                    </button>
                    <button className="flex-1 py-2 w-40 rounded bg-gray-300 border border-blue-200 text-white text-sm" onClick={() => navigate(`/reports/${f.id}`)}>
                      Reports
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* QR Scan Tab */}
        {activeTab === "qrscan" && (
          <section className="space-y-3 bg-gray-600 rounded-lg border border-green-500">
            <div className="flex flex-col items-start justify-between">
              <h2 className="text-lg font-semibold text-yellow-200">QR Scanner</h2>
              <div className="text-sm text-white">Scan plantations, verify survival</div>
            </div>

            <div className="grid grid-cols-1 gap-3 bg-gray-700 place-items-center">
              <div className="rounded-xl bg-gray-300 border border-blue-100 p-3 text-center">
                <div className="h-64 rounded-lg overflow-hidden mb-3">
                  <Scanner
                    onScan={handleScan}
                    onError={handleError}
                    constraints={{
                      video: {
                        facingMode: 'environment' // Use back camera on mobile
                      }
                    }}
                    style={{ width: '100%', height: '100%' }}
                  />
                </div>
                {qrResult && (
                  <p className="text-sm text-green-600 mb-3">Scanned: {qrResult}</p>
                )}
                <p className="text-xs text-white">Hold QR code steady in front of camera</p>
              </div>

              <div className="p-3 rounded-xl bg-gray-800 border border-blue-100">
                <div className="font-semibold text-white">Recent Scans</div>
                <ul className="mt-2 space-y-2 text-sm">
                  <li className="flex items-center justify-between">
                    <span className="text-white">Plantation #12345</span>
                    <span className="text-xs text-green-600">Verified ✓</span>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="text-white">Plantation #67890</span>
                    <span className="text-xs text-yellow-600">Pending</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* SHGs Tab */}
        {activeTab === "shg" && (
          <section className="space-y-3 bg-gray-600 rounded-lg border border-green-500">
            <div className="flex flex-col items-start justify-between">
              <h2 className="text-lg font-semibold text-yellow-200">SHGs & Van Dhan</h2>
              <div className="text-sm text-white">Onboarded: 1,120</div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div className="p-3 rounded-xl bg-gray-800 border border-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">Van Dhan Cluster A</div>
                    <div className="text-xs text-white">Products: Tendu, Mahua, Leaf Plates</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold">Revenue</div>
                    <div className="text-white font-semibold">₹ 1.2L (last month)</div>
                  </div>
                </div>

                <div className="mt-3 flex gap-2">
                  <button onClick={() => navigate('/marketplace')} className="flex-1 py-2 rounded bg-brown-800 text-white text-sm">Marketplace</button>
                  <button onClick={() => navigate('/learning')} className="flex-1 py-2 rounded bg-gray-300 border border-blue-200 text-white text-sm">L&D</button>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-gray-800 border border-blue-100">
                <div className="font-semibold">Community Referral Program</div>
                <div className="text-xs text-white mt-1">Micro-earnings for referrals; connect SHGs to digital buyers</div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => navigate('/referral')} className="py-2 rounded bg-green-700 text-white text-sm w-full">Share Referral</button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Training Tab */}
        {activeTab === "training" && (
          <section className="space-y-3 bg-gray-600 rounded-lg border border-green-500">
            <div className="flex flex-col items-start justify-between">
              <h2 className="text-lg font-semibold text-yellow-200">Training & Skill</h2>
              <div className="text-sm text-white">Frameworks: PMKVY · RPL</div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div className="p-3 rounded-xl bg-gray-800 border border-blue-100">
                <div className="font-semibold">Process Scorecard</div>
                <div className="text-xs text-white mt-1">Track trainees across digital literacy, field skills and enterprise readiness</div>
                <div className="mt-3">
                  <div className="w-full bg-green-100 rounded-full h-3">
                    <div className="h-3 rounded-full" style={{ width: "67%", background: "linear-gradient(90deg,#2f855a,#68d391)" }}></div>
                  </div>
                  <div className="text-xs text-white mt-2">Average readiness: 67%</div>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-gray-800 border border-blue-100">
                <div className="font-semibold">Course Catalog</div>
                <div className="text-xs text-white mt-1">Digital literacy, NTFP value-add, Enterprise & Branding</div>

                <ul className="mt-3 space-y-2 text-sm">
                  <li className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <div className="font-medium">NTFP Value-Addition</div>
                      <div className="text-xs text-white">3 hours • RPL aligned</div>
                    </div>
                    <button onClick={() => navigate('/enroll/ntfp-value-addition')} className="py-1 px-3 w-20 rounded bg-brown-800 text-white text-xs">Enroll</button>
                  </li>

                  <li className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <div className="font-medium">Digital Marketplace Basics</div>
                      <div className="text-xs text-white">2 hours • e-Commerce</div>
                    </div>
                    <button onClick={() => navigate('/enroll/digital-marketplace')} className="py-1 px-3 w-20 rounded bg-brown-800 text-white text-xs">Enroll</button>
                  </li>
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <section className="space-y-3 bg-gray-600 rounded-lg border border-green-500">
            <div className="flex flex-col items-start justify-between">
              <h2 className="text-lg font-semibold text-yellow-200">Analytics & Root-Cause</h2>
              <div className="text-sm text-white">AI-driven insights · Think5Whys engine (demo)</div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 place-items-center">
              <div className="sm:col-span-2 rounded-xl bg-gray-700 border border-blue-100 p-3">
                <div className="font-semibold">Top Issues — Root Cause (sample)</div>
                <ol className="mt-2 list-decimal list-inside text-sm text-white space-y-2">
                  <li>Delayed sapling distribution → Logistics & transport gaps</li>
                  <li>Low QR scanning adoption → Digital literacy + handset availability</li>
                  <li>Market link delays → Packaging & procurement coordination</li>
                </ol>
                <div className="mt-3 text-xs text-white">Use Think5Whys engine to auto-generate recommended actions and owners.</div>
              </div>

              <div className="rounded-xl bg-gray-700 border border-blue-100 p-3">
                <div className="font-semibold">Fund Utilization</div>
                <div className="text-xs text-white mt-1">CAMPA & Project spend</div>
                <div className="mt-3">
                  <div className="text-sm font-semibold">₹ 2.4 Cr</div>
                  <div className="text-xs text-white">Utilized (last FY)</div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Tribal Earnings Tab */}
        {activeTab === "earnings" && (
            <section className="space-y-3 bg-gray-600 rounded-lg border border-green-500">
                <div className="flex flex-col items-start justify-between">
                <h2 className="text-lg font-semibold text-yellow-200">Tribal Digital Earnings</h2>
                <div className="text-sm text-white">Track community income from NTFP, VanDhan, and skill certifications</div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="sm:col-span-2 rounded-xl bg-gray-800 border border-blue-100 p-3">
                    <div className="font-semibold">Top Earning Sources (sample)</div>
                    <ol className="mt-2 list-decimal list-inside text-sm text-white space-y-2">
                    <li>Tendu leaf collection → ₹ 2.1 Cr (45% share)</li>
                    <li>Mahua flower processing → ₹ 1.8 Cr (38% share)</li>
                    <li>Skill certification bonuses → ₹ 0.3 Cr (17% share)</li>
                    </ol>
                    <div className="mt-3 text-xs text-white">Monitor real-time payouts and referral incentives via blockchain ledger.</div>
                </div>

                <div className="rounded-xl bg-gray-800 border border-blue-100 p-3">
                    <div className="font-semibold">Total Payouts</div>
                    <div className="text-xs text-white mt-1">YTD Community Earnings</div>
                    <div className="mt-3">
                    <div className="text-sm font-semibold">₹ 4.2 Cr</div>
                    <div className="text-xs text-white">Distributed (2025)</div>
                    </div>
                </div>
                </div>
            </section>
        )}

        {/* My Dashboard Tab */}
        {activeTab === "dashboard" && (
            <section className="space-y-3 bg-gray-600 rounded-lg border border-green-500">
                <div className="flex flex-col items-start justify-between">
                <h2 className="text-lg font-semibold text-yellow-200">My Dashboard</h2>
                <div className="text-sm text-white">Personalized metrics and quick actions</div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="sm:col-span-2 rounded-xl bg-gray-800 border border-blue-100 p-3">
                    <div className="font-semibold">Recent Personal Activities (sample)</div>
                    <ol className="mt-2 list-decimal list-inside text-sm text-white space-y-2">
                    <li>QR verified 15 plantations → +50 points earned</li>
                    <li>Completed NTFP training module → Certification unlocked</li>
                    <li>Referred SHG member → ₹ 500 bonus pending</li>
                    </ol>
                    <div className="mt-3 text-xs text-white">View full activity log and redeem points for community rewards.</div>
                </div>

                <div className="rounded-xl bg-gray-800 border border-blue-100 p-3">
                    <div className="font-semibold">Achievement Rate</div>
                    <div className="text-xs text-white mt-1">Personal Targets Met</div>
                    <div className="mt-3">
                    <div className="text-sm font-semibold">92%</div>
                    <div className="text-xs text-white">This Month</div>
                    </div>
                </div>
                </div>
            </section>
        )}
      </main>

      {/* Toast Notification */}
      {toastMessage && (
        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }} 
          exit={{ opacity: 0, y: 10 }}
          className="fixed left-1/2 transform -translate-x-1/2 bottom-6 z-50 bg-brown-900 text-white px-6 py-3 rounded-md text-sm shadow-xl border border-green-600"
        >
          {toastMessage}
        </motion.div>
      )}

      {/* Footer */}
      <footer className="bg-[#2a0f2f] border-t border-blue-800 text-blue-300 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs lg:text-sm">
            © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
          </div>
          <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
            <Link to="/about-us" className="text-white hover:text-blue-400">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-blue-400">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-blue-400">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-blue-400">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}